import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

export const PROFILE = {
  id: 'sunburst',
  family: 'graphic',
  danceability: 0.88,
  energyLevel: 0.84,
  bpmMin: 100,
  bpmMax: 140,
  movement: 0.80,
  tonality: 0.80,
  enabled: true
};

// Sunburst : un éclat de rayons pop-art plein écran, le fond d'une affiche
// rétro ou d'une case de bande dessinée. LOOK VDJ, preset party. Pop, house,
// disco, K-pop, électro-pop, funk.
//
// UN QUAD PLEIN ÉCRAN, identique à tous les tiers. Coordonnées polaires
// autour du CENTRE DE COMPOSITION (uStageCenter, R19) - jamais de la pochette,
// qui n'a que le masque safeZone.
//
// LES RAYONS : s = (angle + rotation + déformation(r)) x N / 2pi, en « unités
// de rayon » ; les rayons vont par paires A | B (A sur la fraction < duty de
// la paire). Le bord : la distance en unités de rayon convertie en PIXELS par
// le gradient ANALYTIQUE de s (|ds/dangle| / r, plus la dérivée radiale de la
// déformation) - pas par fwidth, qui saute à la coupure de atan en +-pi. Un
// contour noir de 2 px entre les rayons, le trait d'encre du pop. Près du
// centre, là où un rayon fait moins de quelques pixels, rayons et encre se
// fondent dans leur couleur moyenne : aucun moiré, des bords nets partout où
// un bord se voit.
//
// LES BLOCS : à chaque temps, un bloc de 2 à 6 rayons consécutifs prend d'un
// coup une couleur de la palette ; le bloc suivant part du bord du précédent,
// à CONTRE-SENS de la rotation (la couleur tourne par à-coups). Les deux
// derniers blocs restent (une traîne), le troisième rend ses rayons à leur
// famille ; un bloc fait au plus un cinquième du cercle (l'alternance A | B
// reste dominante). Mesure : A et B échangent leurs couleurs. Palette : quatre couleurs
// pop tirées de la pochette, saturation 1 (R4, A et B à au moins 0,25 de
// teinte), le noir, un blanc crème.
//
// LA TRAME : points de demi-teinte ronds en grille polaire (espacement
// constant, nombre de points par anneau proportionnel au rayon), plus petits
// vers l'extérieur, de la COULEUR du rayon assombrie (la trame ne change pas
// la couleur d'un pixel, elle l'ombre). Une onde part du centre à chaque
// temps et grossit les points à son passage.
//
// CONFORT (R10) : un pixel change de couleur quand un bord de rayon passe sur
// lui, et quand un bloc arrive ou repart. La vitesse angulaire est plafonnée
// pour que les passages de bords + les changements des blocs (+ inversions,
// + l'ondulation des VAGUES) restent sous 6 par seconde ; les inversions
// passent par VdjComfort (3 flashs par seconde au plus).
//
// Debug : ?sunrays=<n>, ?sunwarp=straight|spiral|wave|star.

const RAYS = [12, 16, 24, 32];
const WARPS = ['straight', 'spiral', 'wave', 'star'];
const MAX_CHANGES_PER_S = 6;
const SYNTH_BPM = 120;
const KICK_ZOOM = 0.05;
const POP_DEFAULT = [0xff2d7a, 0xffd21f, 0x1fd0ff, 0xff7a1f];
const CREAM = 0xfff1d6;
const TRAIL = 2;                    // the current block and its trail

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const vertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

const fragment = `
  precision highp float;
  uniform vec2 uRes;
  uniform float uAspect;
  uniform vec2 uStageCenter;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform float uN;
  uniform float uRot;
  uniform float uWarp;          // 0 straight, 1 spiral, 2 wave, 3 star
  uniform float uTwist;         // spiral: radians per height unit
  uniform float uWavePhase;
  uniform float uZoom;
  uniform vec3 uPal[6];         // 0 A, 1 B, 2 3 pop, 4 black, 5 cream
  uniform vec4 uBlock0;         // start ray, length, palette slot, on
  uniform vec4 uBlock1;
  uniform vec4 uBlock2;
  uniform float uMono;          // the drop's black and white beat
  uniform float uScatter;       // the explosion: every ray re-rolled per beat
  uniform float uScatterSeed;
  uniform float uWave;          // the halftone wave's radius (height units)
  uniform float uGain;
  varying vec2 vUv;

  const float TAU = 6.2831853;

  float hash11(float n) {       // bounded input (fp16 TVs): n < 4096
    vec3 p3 = fract(vec3(n) * vec3(0.1031, 0.1030, 0.0973));
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  vec3 slotColor(float s) {
    if (s < 0.5) return uPal[0];
    if (s < 1.5) return uPal[1];
    if (s < 2.5) return uPal[2];
    if (s < 3.5) return uPal[3];
    if (s < 4.5) return uPal[4];
    return uPal[5];
  }
  float inBlock(float k, vec4 b) {
    float d = mod(k - b.x, uN);
    return b.w * step(d, b.y - 0.5);
  }
  // the palette slot of ray k (0 .. N-1): its family, unless a block holds it
  float raySlot(float k) {
    float slot = mod(k, 2.0);
    if (uScatter > 0.5) {
      // the explosion: every ray a fresh pop colour (or cream) on every beat
      float h = hash11(mod(k * 7.0 + uScatterSeed * 13.0, 4093.0));
      return h < 0.85 ? floor(h / 0.85 * 4.0) : 5.0;
    }
    // the oldest block first: the newest wins
    if (inBlock(k, uBlock2) > 0.5) slot = uBlock2.z;
    if (inBlock(k, uBlock1) > 0.5) slot = uBlock1.z;
    if (inBlock(k, uBlock0) > 0.5) slot = uBlock0.z;
    return slot;
  }
  vec3 rayColor(float k) {
    vec3 c = slotColor(raySlot(k));
    if (uMono > 0.5) c = mod(k, 2.0) < 0.5 ? uPal[4] : uPal[5];
    return c;
  }

  void main() {
    // height units, y up, about the centre of composition (R19)
    vec2 s = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    vec2 p = (s - uStageCenter) / uZoom;
    float r = max(length(p), 1e-4);
    float ang = atan(p.y, p.x);
    // the warps read the UNZOOMED radius: the bar's zoom kick scales the
    // halftone, never the edges (a zoomed spiral or wave swings its edges
    // across pixels twice a bar - R10)
    float rw = max(length(s - uStageCenter), 1e-4);
    float px = 2.0 / uRes.y / uZoom;             // one pixel, in height units
    // the warp: an angle offset w(r) and its radial derivative
    float w = 0.0, dw = 0.0;
    if (uWarp > 0.5 && uWarp < 1.5) { w = uTwist * rw; dw = uTwist; }
    else if (uWarp > 1.5 && uWarp < 2.5) {
      // the amplitude stays under half a ray (R10): a wave wider than a ray
      // sweeps several edges over a pixel on every cycle
      float k = 9.0, amp = min(0.10, 0.45 * TAU / uN);
      w = amp * sin(k * rw - uWavePhase); dw = amp * k * cos(k * rw - uWavePhase);
    }
    float sRay = (ang + uRot + w) * uN / TAU;
    // |grad sRay| in ray units per height unit: tangential N/(2 pi r), radial N w'/(2 pi)
    float grad = uN / TAU * sqrt(1.0 / (r * r) + dw * dw);
    float rayPx = 1.0 / (grad * px);              // one ray's width in pixels
    // pairs A | B; the star narrows A to points outward
    float duty = 0.5;
    if (uWarp > 2.5) duty = mix(0.78, 0.16, clamp(rw / 1.9, 0.0, 1.0));
    float q = sRay * 0.5;
    float pair = floor(q), f = q - pair;
    float isB = step(duty, f);
    float k = mod(pair * 2.0 + isB, uN);
    // distance to the nearest edge, in ray units, then in pixels
    float dEdge = min(min(f, abs(f - duty)), 1.0 - f) * 2.0;
    float dPx = dEdge * rayPx;
    vec3 cA = rayColor(k);
    // the neighbour across the nearest edge, for the antialiased blend
    float nb = f < duty ? (f < duty * 0.5 ? k - 1.0 : k + 1.0) : (f - duty < (1.0 - duty) * 0.5 ? k - 1.0 : k + 1.0);
    vec3 cN = rayColor(mod(nb + uN, uN));
    // the ray, antialiased against its neighbour at the edge (half a px each side)
    vec3 col = mix(cN, cA, smoothstep(-0.5, 0.5, dPx));
    // the ink: a black stroke 2 px across the edge (1 on each side)
    float ink = 1.0 - smoothstep(0.5, 1.5, dPx);
    col = mix(col, uPal[4], ink);
    // near the centre the rays are thinner than a few pixels: their mean
    vec3 mean = 0.5 * (rayColor(k - mod(k, 2.0)) + rayColor(k - mod(k, 2.0) + 1.0));
    col = mix(mean, col, smoothstep(2.5, 6.0, rayPx));

    // the halftone: a polar grid of round dots, constant spacing
    float sp = 0.055;
    float ri = floor(r / sp);
    float rc = (ri + 0.5) * sp;
    float M = max(6.0, floor(TAU * rc / sp));
    float aj = floor((ang + 3.14159265) / TAU * M);
    float ac = (aj + 0.5) / M * TAU - 3.14159265;
    vec2 dc = vec2(cos(ac), sin(ac)) * rc;
    float dd = length(p - dc) / px;                       // px to the dot's centre
    float spPx = sp / px;
    float size = mix(0.42, 0.10, clamp(r / 1.9, 0.0, 1.0));
    float wave = exp(-pow((r - uWave) / 0.12, 2.0));
    float rad = spPx * min(0.48, size + 0.22 * wave);
    float dot_ = (1.0 - smoothstep(rad - 0.7, rad + 0.7, dd)) * smoothstep(1.5, 3.0, rad);
    col *= 1.0 - 0.45 * dot_;

    // the safe zone: it follows the cover (R19), the composition does not
    vec2 qz = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(qz, 0.0)) + min(max(qz.x, qz.y), 0.0);
    col *= mix(0.42, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(col * uGain, 1.0);
  }
`;

export class SunburstVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    const nr = parseInt(urlParam('sunrays'), 10);
    this._forcedN = Number.isFinite(nr) ? Math.max(4, Math.min(64, nr - (nr % 2))) : null;
    const wp = urlParam('sunwarp');
    this._forcedWarp = WARPS.indexOf(wp) >= 0 ? WARPS.indexOf(wp) : null;
    // flat graphic colours: no tone mapping, no bloom
    this._post = new VdjPost({ bloomStrength: 0.0, bloomThreshold: 1.0, vignette: 0.12, grain: 0.006, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const pal = [];
    for (let i = 0; i < 6; i++) pal.push(new THREE.Color());
    this._u = {
      uRes: { value: new THREE.Vector2(1280, 720) },
      uAspect: safeZoneUniforms.uAspect, uStageCenter: safeZoneUniforms.uStageCenter,
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather,
      uN: { value: 16 }, uRot: { value: 0 }, uWarp: { value: 0 }, uTwist: { value: 0 }, uWavePhase: { value: 0 }, uZoom: { value: 1 },
      uPal: { value: pal },
      uBlock0: { value: new THREE.Vector4() }, uBlock1: { value: new THREE.Vector4() }, uBlock2: { value: new THREE.Vector4() },
      uMono: { value: 0 }, uScatter: { value: 0 }, uScatterSeed: { value: 0 }, uWave: { value: 9 }, uGain: { value: 1 }
    };
    // a fresh material per instance (one small shader): a cached one would keep
    // the uniform object of its first instance
    this._mat = new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: vertex, fragmentShader: fragment, depthTest: false, depthWrite: false });
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mat);
    this._quad.frustumCulled = false;
    const self = this, size = new THREE.Vector2();
    this._quad.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(size);
      self._u.uRes.value.copy(size);
    };
    scene.add(this._quad);

    this._time = 0; this._beats = 0;
    this._lastBeatPhase = null; this._lastBeatIndex = null; this._lastBarPhase = null; this._lastPhrasePhase = null;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0; this._beatPeriod = 60 / SYNTH_BPM;
    this._eighths = 0;
    this._n = this._forcedN || 16;
    this._warp = this._forcedWarp != null ? this._forcedWarp : 0;
    this._dir = 1; this._rot = 0; this._omega = 0;
    this._twist = 0; this._wavePhase = 0;
    this._blocks = [];               // newest first: { start, len, slot }
    this._blockPos = 0;
    this._swapped = false;
    this._dropAt = -99; this._lastDropKey = null; this._monoUntil = -1; this._scatterUntil = -1;
    this._pop = POP_DEFAULT.map((c) => new THREE.Color(c));
    this._lastPaletteRef = undefined;
    this._kick = 0;
    this._changeRate = 0;
    this._applyPalette(null);
  }

  // --- the palette: four pop colours at full saturation, A and B furthest in hue
  _applyPalette(pal) {
    const src = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors.slice(0, 4) : POP_DEFAULT;
    const hsl = {};
    const pops = src.map((c) => { const k = new THREE.Color(c); k.getHSL(hsl); return { h: hsl.h }; });
    // fill to four hues at least 0.20 apart, rotating the first
    while (pops.length < 4) pops.push({ h: (pops[0].h + 0.25 * pops.length) % 1 });
    for (let i = 1; i < pops.length; i++) for (let j = 0; j < i; j++) {
      let d = Math.abs(pops[i].h - pops[j].h); d = Math.min(d, 1 - d);
      if (d < 0.20) pops[i].h = (pops[j].h + 0.25 + 0.05 * i) % 1;
    }
    // A and B: the pair furthest apart (R4, >= 0.25)
    let best = [0, 1], bd = -1;
    for (let i = 0; i < 4; i++) for (let j = i + 1; j < 4; j++) {
      let d = Math.abs(pops[i].h - pops[j].h); d = Math.min(d, 1 - d);
      if (d > bd) { bd = d; best = [i, j]; }
    }
    const order = [best[0], best[1], ...[0, 1, 2, 3].filter((i) => i !== best[0] && i !== best[1])];
    this._pop = order.map((i) => new THREE.Color().setHSL(pops[i].h, 1.0, 0.52));
    this._writePalette();
  }
  _writePalette() {
    const P = this._u.uPal.value;
    const a = this._swapped ? 1 : 0, b = this._swapped ? 0 : 1;
    P[0].copy(this._pop[a]); P[1].copy(this._pop[b]); P[2].copy(this._pop[2]); P[3].copy(this._pop[3]);
    P[4].setRGB(0, 0, 0); P[5].set(CREAM);
  }

  // --- rhythm ---------------------------------------------------------------------
  // one block step: the next block starts at the edge of the previous one,
  // against the rotation; 2..6 rays; a colour other than the rays' own
  _stepBlock() {
    const n = this._n;
    // 2..6 rays, never more than a fifth of the circle: two blocks leave the
    // A | B alternation most of it
    const maxLen = this._maxLen();
    const len = 2 + Math.floor(this._hash(this._beatCount * 3 + this._eighths) * (maxLen - 1));
    if (this._dir > 0) this._blockPos = ((this._blockPos - len) % n + n) % n;
    else this._blockPos = (this._blockPos + (this._blocks[0] ? this._blocks[0].len : 0)) % n;
    const pick = this._hash(this._beatCount * 7 + 1 + this._eighths * 5);
    const slot = pick < 0.40 ? 2 : pick < 0.80 ? 3 : pick < 0.92 ? 5 : (this._blocks.length % 2);
    this._blocks.unshift({ start: this._blockPos, len, slot });
    if (this._blocks.length > TRAIL) this._blocks.length = TRAIL;
    this._blockSteps = (this._blockSteps || 0) + 1;
  }
  _maxLen() { return Math.max(2, Math.min(6, Math.round(this._n / 5))); }
  _hash(i) { let x = Math.sin(i * 12.9898 + 78.233) * 43758.5453; return x - Math.floor(x); }

  _onBeat() {
    this._beatCount++;
    this._waveStart = this._time;
    if (this._time < this._scatterUntil) this._scatterSeed = (this._scatterSeed || 0) + 1;
    else this._stepBlock();
  }
  _onEighth() { this._eighths++; this._stepBlock(); }
  _onBar(director) {
    this._barCount++;
    this._kick = 1;
    // the swap of A and B: a full-screen change, through the comfort governor;
    // not in the drop (black and white, then every colour changes each beat)
    if (this._time >= this._scatterUntil && this._time + 0.05 >= this._dropAt + 0.1 && this._comfort.requestFlash(this._time)) { this._swapped = !this._swapped; this._writePalette(); }
  }
  _onPhrase() {
    this._phraseCount++;
    if (this._forcedN == null && this._time >= this._scatterUntil) this._n = RAYS[this._phraseCount % RAYS.length];
    if (this._forcedWarp == null) this._warp = this._phraseCount % WARPS.length;
    this._dir = -this._dir;
    this._twist = 0.6;
    this._blocks.length = 0;
  }
  _onDrop(director) {
    this._dropAt = this._time;
    if (this._comfort.requestFlash(this._time) && director) director.flash = Math.max(director.flash || 0, VDJ_DROP_FLASH);
    this._monoUntil = this._time + this._beatPeriod;
    this._scatterUntil = this._monoUntil + 16 * this._beatPeriod;
    if (this._forcedN == null) this._n = Math.min(64, this._n * 2);
    this._blocks.length = 0;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const dt = Math.min(deltaTime, 0.05);
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;
    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    if (paused) return;                  // the frame holds
    this._time += dt;

    // the clocks
    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false, eighthEdge = false;
    const before = this._beats;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true;
      if (this._lastBeatPhase != null && this._lastBeatPhase < 0.5 && beatPhase >= 0.5) eighthEdge = true;
      const bi = profile.beatIndex;
      if (bi != null && this._lastBeatIndex != null) this._beats += Math.max(0, (bi - this._lastBeatIndex) + (beatPhase - this._lastBeatPhase));
      else this._beats += dt / this._beatPeriod;
      this._lastBeatIndex = bi != null ? bi : null;
      this._lastBeatPhase = beatPhase;
    } else {
      this._beats += dt / this._beatPeriod;
      if (Math.floor(this._beats) !== Math.floor(before)) beatEdge = true;
      if (Math.floor(this._beats * 2) !== Math.floor(before * 2) && !beatEdge) eighthEdge = true;
    }
    const ev = director && director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    const montee = beatsToDrop != null && beatsToDrop < 32 ? clamp01((32 - beatsToDrop) / 32) : 0;
    const scatter = this._time < this._scatterUntil;
    // the last beat before the drop holds still (no block, no rotation): a
    // breath, and the drop's cuts do not stack on the montee's (R10)
    const breath = beatsToDrop != null && beatsToDrop < 1.0;
    if (beatEdge && !breath) this._onBeat();
    // the montee: blocks on the eighth notes
    if (eighthEdge && montee > 0 && !scatter && !breath) this._onEighth();
    let barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    let barEdge = false;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) barEdge = true; this._lastBarPhase = barPhase; }
    else { barPhase = (this._beats % 4) / 4; if (Math.floor(this._beats / 4) !== Math.floor(before / 4)) barEdge = true; }
    if (barEdge) this._onBar(director);
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase(); this._lastPhrasePhase = phrasePhase; }
    else if (Math.floor(this._beats / 32) !== Math.floor(before / 32)) this._onPhrase();
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey && this._time - this._dropAt > 2) { this._lastDropKey = key; this._onDrop(director); }
    }
    if (!scatter && this._scatterUntil > 0 && this._forcedN == null && this._n > 32) this._n = RAYS[this._phraseCount % RAYS.length];

    // --- the rotation, capped (R10): a pixel changes colour when an edge
    // crosses it (N per turn) and when the blocks or the swap reach it
    const beatsPerS = 1 / this._beatPeriod;
    const n = this._n;
    const blockRate = (montee > 0 && !scatter ? 2 : 1) * beatsPerS;
    // a ray is in a new block at most once per pass; arrives AND leaves: x2
    const maxLen = this._maxLen();
    // the explosion: every ray recoloured each beat, plus the drop's own cuts
    // (black and white, N doubled) in its first second: x1.5
    let events = scatter ? 1.5 * beatsPerS : 2 * blockRate * Math.min(1, maxLen / n);
    if (!scatter) events += beatsPerS / 4;                     // the swap, once a bar
    // the waves are STILL in time: the edges undulate along the radius and
    // turn with the rays. A wave travelling in time swung each edge back and
    // forth over the pixels near it (measured: a pixel at 7 a second).
    // 3.0 of margin: the events of a bar can fall in the same second as two
    // edge passes (measured: 4 % of pixels at 7-8 with a 0.5 margin, a few at 7 with 1.5 and 2.0; the bar kick zoom moves warped edges; 1-2 pixels at 7 with 2.5, events coinciding at the end of the explosion)
    const crossBudget = Math.max(0.4, MAX_CHANGES_PER_S - 3.0 - events);
    // the spiral's tightening moves the edges too: |dtwist/dt| x r (r <= 2)
    // capped too: at N = 64 an unchecked tightening alone passed 2 edges a
    // second over a pixel; it gets at most half of the budget
    const twistRate = this._warp === 1 ? Math.min(this._twistRate(montee), 0.5 * crossBudget * 2 * Math.PI / (n * 2.0)) : 0;
    const omegaMax = Math.max(0, crossBudget * 2 * Math.PI / n - twistRate * 2.0);
    const want = (2 * Math.PI / 16) * beatsPerS * (1 + 2 * montee) * (scatter ? 3 : 1);
    this._omega = breath ? 0 : Math.min(want, omegaMax);
    this._rot += this._dir * this._omega * dt;
    this._rot = ((this._rot % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    this._changeRate = this._omega * n / (2 * Math.PI) + twistRate * 2.0 * n / (2 * Math.PI) + events;
    // the spiral: a twist that grows over the phrase (and faster in the montee)
    if (this._warp === 1) this._twist = Math.min(2.6, this._twist + twistRate * dt);
    else this._twist = 0.6;

    // the bar's kick: zoom +5 %, eased out over the first beat
    const u0 = Math.min(1, Math.max(0, barPhase) * 4);
    const kz = (1 - u0) * (1 - u0) * (1 - u0);
    // the beat's RGB split, at 40 %: at full strength its fringes, widest in
    // the corners, recoloured the pixels along every edge on every beat (R10)
    this._post.update(1 + 0.4 * (vdjBeatChroma(beatPhase != null ? beatPhase : this._beats % 1, this._beatPeriod) - 1));

    const U = this._u;
    U.uN.value = n;
    U.uRot.value = this._rot;
    U.uWarp.value = this._warp;
    U.uTwist.value = this._twist * this._dir;
    U.uWavePhase.value = this._wavePhase;
    U.uZoom.value = 1 + KICK_ZOOM * kz * (this._barCount > 0 ? 1 : 0);
    const B = [U.uBlock0.value, U.uBlock1.value, U.uBlock2.value];
    for (let i = 0; i < TRAIL; i++) {
      const b = this._blocks[i];
      if (b && !scatter && this._time >= this._monoUntil) B[i].set(b.start, b.len, b.slot, 1); else B[i].set(0, 0, 0, 0);
    }
    U.uMono.value = this._time < this._monoUntil ? 1 : 0;
    U.uScatter.value = scatter && this._time >= this._monoUntil ? 1 : 0;
    U.uScatterSeed.value = (this._scatterSeed || 0) % 300;
    // the halftone wave: leaves the centre on every beat, reaches the corners in half a beat
    const wt = this._waveStart != null ? (this._time - this._waveStart) / (0.6 * this._beatPeriod) : 9;
    U.uWave.value = wt * 2.2;
    // comfort: flat colours fill the frame; the governor sees the palette's mean
    const lum = (c) => 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
    const P = U.uPal.value;
    // measure() expects the luminance AFTER the current gain: fed the raw estimate, a
    // bright palette (mean > 0.53) kept it over the target and the governor multiplied
    // the gain by 0.7 on every frame - a black screen within a second on bright covers
    const est = (0.5 * (lum(P[0]) + lum(P[1])) * 0.85 * 0.9) * this._comfort.gain + (director && director.flash ? director.flash * 0.6 : 0);
    U.uGain.value = this._comfort.measure(est, dt);
  }

  _twistRate(montee) {
    // radians per height unit per second: +0.5 rad over a phrase, x3 in the montee
    return (0.5 / (32 * this._beatPeriod)) * (1 + 2 * montee);
  }

  getProbe() {
    return {
      t: +this._time.toFixed(2), beats: +this._beats.toFixed(2), n: this._n, warp: WARPS[this._warp], dir: this._dir,
      rot: +this._rot.toFixed(3), omega: +this._omega.toFixed(3), changeRate: +this._changeRate.toFixed(2),
      blocks: this._blocks.map((b) => [b.start, b.len, b.slot]), blockSteps: this._blockSteps || 0, beatCount: this._beatCount,
      swapped: this._swapped, mono: this._time < this._monoUntil, scatter: this._time < this._scatterUntil,
      twist: +this._twist.toFixed(3), wave: +this._u.uWave.value.toFixed(2), gain: +this._comfort.gain.toFixed(3)
    };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); }
    if (this._mat) this._mat.dispose();
    this._scene = null;
  }
}
